import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Globe } from "lucide-react";
import { SUPPORTED_LANGUAGES } from "@/contexts/language-context";
import { useLanguage } from "@/hooks/use-language";

const VARIANT_STYLES = {
  default: {
    button:
      "flex items-center gap-2 text-white/60 hover:text-white transition-colors px-3 py-2 rounded-full border border-white/10 hover:border-white/20",
    menu:
      "absolute right-0 top-full mt-2 w-44 bg-[#0a0520]/95 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden shadow-[0_0_30px_rgba(0,0,0,0.5)]",
    active: "text-[#FF2E63] bg-[#FF2E63]/10",
    idle: "text-white/60 hover:text-white hover:bg-white/5",
  },
  cinematic: {
    button:
      "inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#FF2E63]/60 bg-black/20 text-white transition-all hover:bg-[#FF2E63]/10 hover:shadow-[0_0_30px_rgba(255,46,99,0.35)]",
    menu:
      "absolute right-0 top-full mt-3 w-44 overflow-hidden rounded-2xl border border-[#FF2E63]/25 bg-[#050315]/95 backdrop-blur-xl shadow-[0_0_30px_rgba(0,0,0,0.55)]",
    active: "text-[#FF2E63] bg-[#FF2E63]/10",
    idle: "text-white/65 hover:text-white hover:bg-white/5",
  },
};

export const LanguageSelector = ({
  variant = "default",
  iconOnly = false,
  className = "",
  buttonTestId = "lang-toggle",
  optionTestIdPrefix = "lang",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { lang, setLang } = useLanguage();
  const menuRef = useRef(null);
  const styles = VARIANT_STYLES[variant] ?? VARIANT_STYLES.default;
  const currentLang =
    SUPPORTED_LANGUAGES.find((language) => language.code === lang) ??
    SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  return (
    <div className={`relative ${className}`} ref={menuRef}>
      <button
        type="button"
        onClick={() => setIsOpen((value) => !value)}
        className={styles.button}
        aria-label={`Language: ${currentLang.label}`}
        aria-expanded={isOpen}
        data-testid={buttonTestId}
        title={currentLang.label}
      >
        <Globe className={iconOnly ? "h-4 w-4" : "h-4 w-4"} />
        {!iconOnly && (
          <>
            <span className="text-sm font-medium">
              {currentLang.code.toUpperCase()}
            </span>
            <ChevronDown
              className={`h-3 w-3 transition-transform ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className={styles.menu}
          >
            {SUPPORTED_LANGUAGES.map((language) => (
              <button
                key={language.code}
                type="button"
                onClick={() => {
                  setLang(language.code);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                  lang === language.code ? styles.active : styles.idle
                }`}
                data-testid={`${optionTestIdPrefix}-${language.code}`}
              >
                <span className="text-base">{language.flag}</span>
                <span className="font-medium">{language.label}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
